import Button from 'components/Button';
import { FC, useEffect, useRef, useState } from 'react';
import Cropper from 'react-cropper';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { black, border1, exactWhite } from 'utils/style/colors';

const ImageCropperContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  gap: 1rem;
`;

const FilePickerInputContainer = styled.div`
  & > label {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background: ${black};
    color: ${exactWhite};
    border-radius: 0.5rem;
    cursor: pointer;
  }
`;

const ImagesContainer = styled.div`
  display: flex;
  gap: 0.5rem;

  & > * {
    width: 50%;
    border-radius: 1rem;
    border: ${border1};
    margin: 0 auto;
  }

  & img {
    height: 20rem;
    margin: 1rem;
    border-radius: 1rem;
  }

  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
`;

const ImageHeader = styled.h3`
  font-weight: bold;
  text-align: center;
  padding: 1rem 0;
`;

interface IProps {
  aspectRatio: number;
  onCropped: (blob: any) => any;
  imageSrc?: string;
  onSetPreview: (preview: string) => void;
}

const ImageCropper: FC<IProps> = ({
  aspectRatio,
  onCropped,
  imageSrc,
  onSetPreview,
}) => {
  const { t } = useTranslation('modals');

  const cropperRef = useRef<any>(null);
  const [src, setSrc] = useState<string>('');

  useEffect(() => {
    if (imageSrc) setSrc(imageSrc);
  }, [imageSrc]);

  const handleFileChange = (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setSrc(url);
  };

  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    if (!canvas) return;

    canvas.toBlob((blob: any) => {
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      onSetPreview(url);
      onCropped(blob);
    }, 'image/png');
  };

  return (
    <ImageCropperContainer>
      {!imageSrc && (
        <FilePickerInputContainer>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          <label htmlFor="file-upload">{t('chooseImage')}</label>
        </FilePickerInputContainer>
      )}

      <ImagesContainer>
        {src && (
          <ImageContainer>
            <ImageHeader>{t('selectedPicture')}</ImageHeader>
            <Cropper
              ref={cropperRef}
              src={src}
              style={{}}
              aspectRatio={aspectRatio}
              guides={true}
              viewMode={1}
              dragMode="move"
              background={false}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false}
            />
          </ImageContainer>
        )}
      </ImagesContainer>

      {src && (
        <div>
          <Button onClick={handleCrop} color="black-outline">
            {t('cropSelectedArea')}
          </Button>
        </div>
      )}
    </ImageCropperContainer>
  );
};

export default ImageCropper;
